/**
 * "I have confirmed which floor this robot is on" — the press that
 * releases a robot held after a fire alarm.
 *
 * WHAT IT ENDS. When a fire is raised the fleet adapter interrupts the
 * robot, and when the alarm clears it does NOT resume by itself: a
 * robot that sat through an evacuation may have been pushed out of a
 * lift or carried, and RMF would otherwise replan from a position
 * nobody has checked. The adapter holds and raises the issue "confirm
 * which floor this robot is on". Nothing else ends that hold — not a
 * timer, not the alarm clearing — so without this press the robot
 * stands until somebody restarts the adapter.
 *
 * IT DOES NOT RELOCALIZE THE ROBOT, despite the verb's name. It asserts
 * that the position the robot is already reporting is right. A robot
 * that is genuinely lost needs the vendor's own relocalisation first;
 * the issue stays raised either way until this is pressed.
 *
 * SHOWN ONLY WHILE THE ISSUE IS RAISED. The hold is invisible from the
 * robot's status — it looks idle — so the issue is the only signal the
 * console has, and offering the button without it would invite a press
 * that releases nothing while reporting success.
 *
 * ADMISSION IS NOT THE OUTCOME, the rule every command surface here is
 * built on: the worker resolves no further than `accepted` because the
 * adapter answers only by resuming the task, minutes later, through a
 * different subscription. So `accepted` renders as an explicit "not
 * confirmed" with what to watch instead.
 *
 * TWO WORDINGS, ONE PRESS (Akash, 2026-10-12). The same verb also ends
 * the pause after a robot's e-stop handed a lift back with it inside
 * (fleet adapter 0.7.19, issue `relocalize after lift`). There the
 * person pressing is hotel staff who have just scanned the robot at a
 * QR point, so `variant="scanned_it"` renders the button as
 * "Scanned it" and its banners in hotel words: no codes, no "RMF", no
 * server text (which can carry either). What is SENT does not change.
 *
 * THE FIRE HOLD, SPLIT BY AUDIENCE (batch B, Akash 2026-10-31). The
 * default wording is the fire hold's. Ops keep it as it was; a site
 * reader gets `FIRE_HOLD_SITE_WORDS` instead of "Release fire hold:
 * refused (REJECTED)", the server's reason and a raw disabled reason.
 * The button keeps its label for both, and the press stays ONE tap with
 * no confirm (decided 2026-10-31): the floor check is the panel's
 * sentence above it.
 */
import { useQuery } from '@tanstack/react-query';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { ROBOT_FIRE_RELOCALIZE_ROLES } from '../../auth/types';
import { useFireRelocalize } from '../../api/hooks/useCommissionRobot';
import { useSafeSiteId } from '../../state/useActiveSite';
import { capabilitiesQueryOptions } from '../../api/query-options';
import { networkFailureText } from '../../api/v1-client';
import type { CommandRecord, SiteId } from '../../api/v1';
import { SCANNED_IT_BUTTON } from '../../state/attention/robot-copy';
import {
  refusedCommandFromError,
  verdictFromError,
  type OperatorVerdict,
} from '../Tasks/task-command-verdict';
import '../Tasks/TaskDetailDrawer.css';

function relocalizeOutcome(
  command: CommandRecord,
): OperatorVerdict | { kind: 'pending'; message: string; detail: string | null } {
  const detail = command.outcome_message ?? null;
  const base = {
    verb: 'Release fire hold',
    request_id: command.correlation?.rmf_request_id ?? null,
    wait_name: null,
    phase: null,
    ts: Date.now(),
    preflight: false,
  } as const;
  if (command.state === 'observed_applied') {
    return {
      kind: 'verdict',
      ...base,
      status: 'done',
      code: null,
      reason: detail ?? 'The robot has been released.',
    };
  }
  if (command.state === 'rejected') {
    return {
      kind: 'verdict',
      ...base,
      status: 'refused',
      code: 'REJECTED',
      reason: detail ?? 'The release was refused.',
    };
  }
  if (command.state === 'timed_out' || command.state === 'outcome_unknown') {
    return {
      kind: 'verdict',
      ...base,
      status: 'timeout',
      code: command.state === 'outcome_unknown' ? 'OUTCOME_UNKNOWN' : 'TIMED_OUT',
      reason:
        detail ??
        'Nothing answered. The robot may still be held; if the issue is still showing in ' +
          'a minute, press again.',
    };
  }
  return {
    kind: 'pending',
    message:
      // No topic name: the fire-hold section shows this to whoever holds
      // the release, hotel supervisors included, and a backticked
      // `robot_action_confirmations` told them nothing (fire-flow review
      // 2026-10-22).
      'Sent — NOT yet released. The confirmation is on its way to the robot; the hold is ' +
      'not lifted until the robot starts moving again and this section goes away. If it is ' +
      'still held in a minute, press again.',
    detail,
  };
}

/** Which words the press is dressed in. `confirm_floor` is the fire
 *  hold's (and ops'); `scanned_it` is hotel staff's, after a QR scan. */
export type FireRelocalizeVariant = 'confirm_floor' | 'scanned_it';

/** One press's words in the hotel's voice: the button's hover, and each
 *  outcome's heading and sentence. */
interface HotelWords {
  title: string;
  unavailable: string;
  pendingHeading: string;
  pending: string;
  done: { heading: string; reason: string };
  refused: { heading: string; reason: string };
  timeout: { heading: string; reason: string };
  failed: { heading: string; reason: string };
}

/** The hotel's words for each outcome of a "Scanned it" press. Fixed
 *  text, never the server's: a refusal reason or an error message can
 *  carry a code, and a site audience reads none. */
const SCANNED_IT_WORDS: HotelWords = {
  title:
    'Press this only after you have scanned it at a QR code. It tells the system it is back on the map, so its job carries on.',
  unavailable: 'This can’t be pressed right now. Call Nexsoss.',
  pendingHeading: 'Sent',
  pending:
    'Its job carries on once it starts moving again and this section goes away. ' +
    `If it is still here in a minute, press ${SCANNED_IT_BUTTON} again.`,
  done: { heading: 'Back on the map', reason: 'Its job is carrying on.' },
  refused: {
    heading: 'Not accepted',
    reason: 'The system did not accept it. If its job still hasn’t carried on, call Nexsoss.',
  },
  timeout: {
    heading: 'No answer yet',
    reason: `Nothing answered. If this section is still here in a minute, press ${SCANNED_IT_BUTTON} again.`,
  },
  failed: {
    heading: 'Not sent',
    reason: 'It could not be sent. Press again; if it keeps failing, call Nexsoss.',
  },
};

/** The fire hold's release in the hotel's words (batch B, 2026-10-31).
 *  "Not released yet" stays the pending heading: the cards and the
 *  manual quote it. A refusal sends the reader back to the panel's QR
 *  steps before the call: the hotel recovers its own robots (D26), and
 *  Nexsoss is for a fix that keeps failing. */
const FIRE_HOLD_SITE_WORDS: HotelWords = {
  title:
    'Press this when it is standing on the floor shown in Location. It carries on from there.',
  unavailable: 'This can’t be pressed right now. Call Nexsoss.',
  pendingHeading: 'Not released yet',
  pending:
    'Sent. It is released when it starts moving and the Held after fire alarm section goes ' +
    'away. If it is still here in a minute, press again.',
  done: { heading: 'Released', reason: 'It is no longer held.' },
  refused: {
    heading: 'Not released',
    reason:
      'The system did not accept it. Check it is on the floor shown, or put it back on the map ' +
      'with the steps above, then press again. If it is refused again, call Nexsoss.',
  },
  timeout: {
    heading: 'No answer yet',
    reason: 'If the Held after fire alarm section is still here in a minute, press again.',
  },
  failed: {
    heading: 'Not sent',
    reason: 'It could not be sent. Press again; if it keeps failing, call Nexsoss.',
  },
};

export function FireRelocalizeAction({
  fleet,
  robot,
  variant = 'confirm_floor',
}: {
  fleet: string;
  robot: string;
  variant?: FireRelocalizeVariant;
}) {
  const hotel = variant === 'scanned_it';
  const { roles } = useAuth();
  // Whose words: "Scanned it" is always the hotel's; the fire hold's are
  // the hotel's for a site reader and ops' own otherwise.
  const words: HotelWords | null = hotel
    ? SCANNED_IT_WORDS
    : audienceFor(roles) === 'site'
      ? FIRE_HOLD_SITE_WORDS
      : null;
  const release = useFireRelocalize();
  const siteId = useSafeSiteId();
  const capabilities = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const cap = (capabilities.data?.capabilities ?? []).find(
    (c) => c.capability === 'robot.fire_relocalize',
  );
  // Disabled only once the probe has ANSWERED: a robot held in a lift
  // lobby after an evacuation is the wrong place to discover that a
  // capability probe timed out. The server stays the authority.
  const disabled = release.isPending || (capabilities.data !== undefined && cap?.enabled === false);

  // The press this banner belongs to, read off the mutation's own
  // variables so it can never sit under a different robot — this
  // component is re-used in place as the drawer's robot changes.
  const sentFor = release.variables;
  const belongsHere = sentFor?.fleet === fleet && sentFor?.robot === robot;
  const outcome = (() => {
    if (!belongsHere) return null;
    if (release.isError) {
      const refused = refusedCommandFromError(release.error);
      if (refused) return relocalizeOutcome(refused);
      const blocked = networkFailureText(release.error);
      const verdict = verdictFromError('Release fire hold', release.error);
      return blocked ? { ...verdict, reason: blocked } : verdict;
    }
    return release.data ? relocalizeOutcome(release.data.command) : null;
  })();

  return (
    <RoleGuard allowedRoles={[...ROBOT_FIRE_RELOCALIZE_ROLES]}>
      <div className="nx-task-drawer__actions">
        <button
          type="button"
          className="nx-task-drawer__action"
          onClick={() => release.mutate({ fleet, robot })}
          disabled={disabled}
          title={
            // A disabled reason is a code (`unsupported_by_rmf`): ops read
            // it, a site audience reads the plain instruction instead.
            words
              ? cap?.enabled === false
                ? words.unavailable
                : words.title
              : cap?.enabled === false
                ? cap.disabled_reasons.join(', ')
                : `Confirm that ${robot} is on the floor it is reporting, so it can resume.`
          }
          data-testid="fire-relocalize-btn"
          data-variant={variant}
        >
          {release.isPending ? 'Sending…' : hotel ? SCANNED_IT_BUTTON : 'Confirm floor and release'}
        </button>
      </div>
      {outcome?.kind === 'pending' && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--pending"
          data-testid="fire-relocalize-pending"
          data-state={release.data?.command.state ?? ''}
          role="status"
        >
          <strong>{words ? `⏳ ${words.pendingHeading}` : '⏳ Not released yet'}</strong>
          <p>{words ? words.pending : outcome.message}</p>
          {!words && outcome.detail && <p className="nx-task-drawer__sub">{outcome.detail}</p>}
        </div>
      )}
      {outcome?.kind === 'verdict' && (
        <div
          className={`nx-task-drawer__banner nx-task-drawer__banner--${outcome.status}`}
          data-testid="fire-relocalize-verdict"
          data-status={outcome.status}
          data-code={outcome.code ?? ''}
          // A refusal or a failure must interrupt a screen reader: it
          // contradicts the button just pressed. Only "done" is
          // conversational. Same rule as `DustbagConfirmAction`.
          role={outcome.status === 'done' ? 'status' : 'alert'}
        >
          {words ? (
            <>
              <strong>{words[outcome.status].heading}</strong>
              <p>{words[outcome.status].reason}</p>
            </>
          ) : (
            <>
              <strong>
                Release fire hold: {outcome.status}
                {outcome.code ? ` (${outcome.code})` : ''}
              </strong>
              <p>{outcome.reason}</p>
            </>
          )}
        </div>
      )}
    </RoleGuard>
  );
}
